import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useClientStore from '../stores/client';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ECharts from 'echarts-for-react';

import alarm from '../assets/sound/beep1.mp3';

import ttsAudio from '../assets/sound/tts_07.mp3';
import ttsAudio_en from '../assets/sound/en/7.mp3';
import ttsAudio_jp from '../assets/sound/jp/7.mp3';
import ttsAudio_cn from '../assets/sound/cn/7.mp3';
import ttsAudio_mn from '../assets/sound/mn/8.mp3';

import BGM from '../assets/sound/MP_1.mp3';

const StressStart2 = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const savedState = (() => {
        try {
            const raw = sessionStorage.getItem('stressStartState');
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            return {};
        }
    })();

    const navState = location.state || savedState || {};

    const device_sn = navState.device_sn;
    const device_no = navState.device_no;
    const member_no = navState.member_no;
    const name = navState.name;
    const sex = navState.sex;
    const birth = navState.birth;
    const returnPath = navState.returnPath || '/monitoring/realtimelist';

    const { client, addOnMessage, removeOnMessage } = useClientStore();

    const [audio] = useState(new Audio(alarm));
    const audioRef = useRef(null);
    const bgmRef = useRef(null);

    const doneRef = useRef(false);
    const topicRef = useRef('');
    const oldRespRef = useRef(0);
    const oldHeartRef = useRef(0);
    const lastMessageAtRef = useRef(0);
    const startTimeRef = useRef(Date.now());

    const [data, setData] = useState({});
    const [values1, setValues1] = useState([]);
    const [label1, setLabel1] = useState([]);
    const [value1, setValue1] = useState(0);

    const [values2, setValues2] = useState([]);
    const [label2, setLabel2] = useState([]);
    const [value2, setValue2] = useState(0);

    const [values3, setValues3] = useState([]);
    const [label3, setLabel3] = useState([]);
    const [value3, setValue3] = useState(0);

    const [value4, setMotionDetected] = useState(0);

    const SEC = 1200;
    const [percent, setPercent] = useState(0);
    const [date] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));
    const start_date = moment(date).utc().format('YYYY-MM-DD HH:mm:ss');

    const [remainSec, setRemainSec] = useState(1200);

    useEffect(() => {
        if (location.state) {
            sessionStorage.setItem('stressStartState', JSON.stringify(location.state));
        }
    }, [location.state]);

    useEffect(() => {
        console.log('===== stress_start state =====');
        console.log('device_sn =', device_sn);
        console.log('device_no =', device_no);
        console.log('member_no =', member_no);
        console.log('name =', name);
        console.log('sex =', sex);
        console.log('birth =', birth);
        console.log('returnPath =', returnPath);
    }, [device_sn, device_no, member_no, name, sex, birth, returnPath]);

    useEffect(() => {
        if (!device_sn || !device_no || !member_no) {
            alert('측정에 필요한 정보가 없습니다.');
            navigate('/monitoring/realtimelist');
        }
    }, [device_sn, device_no, member_no, navigate]);

    useEffect(() => {
        const timer = setInterval(() => {
            setRemainSec(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const formatTime = sec => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;

        const mm = String(m).padStart(2, '0');
        const ss = String(s).padStart(2, '0');

        return `${mm}:${ss}`;
    };

    useEffect(() => {
        const audioEl = audioRef.current;
        if (audioEl) {
            audioEl.play().catch(err => {
                console.warn('Autoplay failed:', err);
            });
        }

        const bgmEl = bgmRef.current;
        if (bgmEl) {
            bgmEl.volume = 0.25;
            bgmEl.loop = true;
            bgmEl.play().catch(err => {
                console.warn('BGM autoplay failed:', err);
            });
        }
    }, []);

    const onMessage = (topic, message) => {
        console.log('MQTT message topic =', topic);

        if (topicRef.current !== topic) {
            console.log('topic mismatch', topicRef.current, topic);
            return;
        }

        try {
            const msg = JSON.parse(message);
            console.log('MQTT payload =', msg);

            if (msg.e) {
                lastMessageAtRef.current = Date.now();
                procData(data, msg.e);
            }
        } catch (e) {
            console.error('MQTT message parse error', e);
        }
    };

    useEffect(() => {
        console.log('client =', client);
        console.log('device_sn for subscribe =', device_sn);

        if (!client) {
            console.log('MQTT client not ready');
            return;
        }

        if (!device_sn) {
            console.log('device_sn missing');
            return;
        }

        const topic = `SmartMat/${device_sn}/Autowells-server/pub_device_data`;
        console.log('subscribe topic =', topic);

        topicRef.current = topic;

        client.subscribe(topic, error => {
            if (error) {
                console.log('subscribe error =', error);
                return;
            }
            console.log('subscribe success =', topic);
        });

        addOnMessage(topic, onMessage);

        return () => {
            console.log('unsubscribe topic =', topic);

            removeOnMessage(topic);

            client.unsubscribe(topic, error => {
                if (error) {
                    console.log('unsubscribe error =', error);
                }
            });
        };
    }, [client, device_sn]);

    useEffect(() => {
        const timer = setInterval(() => {
            const diff = Date.now() - lastMessageAtRef.current;
            const online = diff < 5000;

            setData(prev => {
                if (!prev || Object.keys(prev).length === 0) {
                    return { online };
                }
                if (prev.online === online) return prev;
                return { ...prev, online };
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        startTimeRef.current = Date.now();

        const totalMilliseconds = SEC * 1000;
        const intervalMilliseconds = 150;
        const progressIncrement = 100 / (totalMilliseconds / intervalMilliseconds);

        const interval = setInterval(() => {
            setPercent(prevPercent => {
                if (prevPercent >= 100) {
                    if (!doneRef.current) {
                        doneRef.current = true;
                        clearInterval(interval);
                        sendData();
                    }
                    return 100;
                }
                return prevPercent + progressIncrement;
            });
        }, intervalMilliseconds);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        return () => {
            try {
                if (bgmRef.current) {
                    bgmRef.current.pause();
                    bgmRef.current.currentTime = 0;
                }
            } catch {}

            if (audio) audio.pause();
        };
    }, [audio]);

    const sendData = () => {
        const end_date = moment().utc().format('YYYY-MM-DD HH:mm:ss');

        console.log('measurement finished');
        console.log({
            device_sn,
            device_no,
            member_no,
            name,
            sex,
            birth,
            start: start_date,
            end: end_date,
            returnPath,
        });

        navigate('/stress/result-ready', {
            state: {
                device_sn,
                device_no,
                member_no,
                name,
                sex,
                birth,
                start: start_date,
                end: end_date,
                returnPath,
            },
        });
    };

    const procData = (currentData, recieveData) => {
        let heart = recieveData[0];
        let resp = recieveData[1];
        let apnea = recieveData[2];
        let state = recieveData[3];
        let fsr_sum = recieveData[4];
        let motion_detected = 0;

        if (resp < 9) {
            resp = oldRespRef.current;
        } else {
            oldRespRef.current = resp;
        }

        if (heart < 56) {
            heart = oldHeartRef.current;
        } else {
            oldHeartRef.current = heart;
        }

        if (recieveData.length >= 11) {
            motion_detected = recieveData[11];
            setMotionDetected(motion_detected);
        }

        const isUse = fsr_sum >= 300;

        if (!isUse) {
            heart = 0;
            resp = 0;
            apnea = 0;
            state = 0;
        }

        const time = moment().format('mm:ss');

        setValue1(Number(heart || 0));
        setValue2(Number(resp || 0));
        setValue3(Number(fsr_sum || 0));

        setValues1(prev => [...prev, Number(heart || 0)].slice(-60));
        setValues2(prev => [...prev, Number(resp || 0)].slice(-60));
        setValues3(prev => [...prev, Number(fsr_sum || 0)].slice(-60));

        setLabel1(prev => [...prev, time].slice(-60));
        setLabel2(prev => [...prev, time].slice(-60));
        setLabel3(prev => [...prev, time].slice(-60));

        setData(prev => ({
            ...(prev || {}),
            online: true,
        }));
    };

    const heartColor = '#F75C5D';
    const breathColor = '#3889ff';
    const oxygenColor = '#3fd3d4';

    const getOption1 = () => ({
        tooltip: {
            trigger: 'axis',
            formatter: `{c} BPM`,
        },
        xAxis: {
            boundaryGap: false,
            data: label1,
        },
        yAxis: {
            type: 'value',
            min: 0,
        },
        grid: {
            top: 30,
            left: 50,
            right: 30,
            bottom: 40,
        },
        series: [
            {
                name: '심박수',
                type: 'line',
                smooth: true,
                symbol: 'none',
                data: values1,
                lineStyle: {
                    color: heartColor,
                    width: 4,
                },
            },
        ],
    });

    const getOption2 = () => ({
        tooltip: {
            trigger: 'axis',
            formatter: `{c} BPM`,
        },
        xAxis: {
            boundaryGap: false,
            data: label2,
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: 40,
        },
        grid: {
            top: 30,
            left: 50,
            right: 30,
            bottom: 40,
        },
        series: [
            {
                name: '호흡수',
                type: 'line',
                smooth: true,
                symbol: 'none',
                data: values2,
                lineStyle: {
                    color: breathColor,
                    width: 4,
                },
            },
        ],
    });

    const getOption3 = () => ({
        tooltip: {
            trigger: 'axis',
            formatter: `{c}`,
        },
        xAxis: {
            boundaryGap: false,
            data: label3,
        },
        yAxis: {
            type: 'value',
            min: 0,
        },
        grid: {
            top: 30,
            left: 50,
            right: 30,
            bottom: 40,
        },
        series: [
            {
                name: '입력값',
                type: 'line',
                smooth: true,
                symbol: 'none',
                data: values3,
                lineStyle: {
                    color: oxygenColor,
                    width: 4,
                },
            },
        ],
    });

    const isUse = value3 >= 300;
    const heartClassName = value4 === 1 ? 'h-motion-detected' : 'heart';
    const breathClassName = value4 === 1 ? 'r-motion-detected' : 'breath';
    const lng = i18n.language;

    return (
        <div className="stress_content">
            <main className="sub-contents">
                {/* {lng === 'ko' && <audio ref={audioRef} src={ttsAudio} autoPlay />}
            {lng === 'en' && <audio ref={audioRef} src={ttsAudio_en} autoPlay />}
            {lng === 'jp' && <audio ref={audioRef} src={ttsAudio_jp} autoPlay />}
            {lng === 'cn' && <audio ref={audioRef} src={ttsAudio_cn} autoPlay />}
            {lng === 'mn' && <audio ref={audioRef} src={ttsAudio_mn} autoPlay />} */}

                <audio ref={bgmRef} src={BGM} autoPlay loop preload="auto" />

                <div className="inner" style={{ maxWidth: '1420px' }}>
                    <div className="progressbar">
                        <h2>{t('20분 스트레스 측정중')}</h2>

                        <span className="remain-time">남은시간 {formatTime(remainSec)}</span>

                        <div
                            style={{
                                width: `100%`,
                                height: '60px',
                                backgroundColor: '#e5e5e5',
                                borderRadius: '999px',
                                overflow: 'hidden',
                                margin: 0,
                            }}
                        >
                            <div
                                style={{
                                    width: `${percent}%`,
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #108ee9 0%, #87d068 100%)',
                                    transition: 'width 0.15s linear',
                                }}
                            />
                        </div>
                    </div>

                    <section className="monitoring_signal_list">
                        <ul>
                            <li className={heartClassName}>
                                <p className="title">
                                    <b>{t('심박수')}</b>(BPM)
                                </p>
                                <p className="data">
                                    <strong>{value1}</strong> BPM
                                </p>
                            </li>

                            <li className={breathClassName}>
                                <p className="title">
                                    <b>{t('호흡수')}</b>(BPM)
                                </p>
                                <p className="data">
                                    <strong>{value2}</strong> BPM
                                </p>
                            </li>

                            <li className="oxygen">
                                <p className="title">
                                    <b>{t('사용 여부')}</b>
                                </p>
                                <p className="data">
                                    <strong>{data?.online ? (isUse ? (value4 === 1 ? t('움직임 감지') : t('사용 중')) : t('미사용')) : t('OFF')}</strong>
                                </p>
                            </li>
                        </ul>
                    </section>

                    <section className="monitoring_time_graph">
                        <h4>{t('실시간 생체신호')}</h4>

                        <div className="graph_wrap">
                            <article className="graph_arti heart_graph">
                                <div className="top">
                                    <span>
                                        <em></em> {t('심박수')}
                                    </span>
                                </div>
                                <div className="graph_box">
                                    <ECharts option={getOption1()} opts={{ renderer: 'svg', width: 'auto' }} />
                                </div>
                            </article>

                            <article className="graph_arti breath_graph">
                                <div className="top">
                                    <span>
                                        <em></em> {t('호흡수')}
                                    </span>
                                </div>
                                <div className="graph_box">
                                    <ECharts option={getOption2()} opts={{ renderer: 'svg', width: 'auto' }} />
                                </div>
                            </article>

                            <article className="graph_arti oxygen_graph">
                                <div className="top">
                                    <span>
                                        <em></em> {t('입력값')}
                                    </span>
                                </div>
                                <div className="graph_box">
                                    <ECharts option={getOption3()} opts={{ renderer: 'svg', width: 'auto' }} />
                                </div>
                            </article>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default StressStart2;
